import Axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import storage from 'utils/storage';
import { API_URL } from 'config/env';
import { toastDuration } from 'config/toast';
import { SessionStorageKey } from 'hooks/useSessionStorage';
import { BackendAppRoutes, ProtectedRoutes } from 'types/Routes';
import { toaster } from 'components/ui/toaster';
import { StatusCode } from '@novaheal/types';
import { refreshUserToken } from './auth';

// TODO: add proper types
// Create proper types for backend API routes
const LOGIN_ROUTES = [
  BackendAppRoutes.AUTH_LOGIN,
  BackendAppRoutes.GOOGLE_AUTHENTICATION_LOGIN,
  BackendAppRoutes.APPLE_AUTHENTICATION_REGISTER,
];

const REGISTER_ROUTES = [
  BackendAppRoutes.AUTH_REGISTER,
  BackendAppRoutes.GOOGLE_AUTHENTICATION_REGISTER,
  BackendAppRoutes.APPLE_AUTHENTICATION_REGISTER,
];

// Add marketing modal ID to login requests
function expandLoginDataRequest(config: InternalAxiosRequestConfig): void {
  if (!config.url || !LOGIN_ROUTES.some((route) => config.url?.includes(route)) || !config.data) {
    return;
  }

  const marketingModalSessionId = sessionStorage.getItem(
    SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID
  );

  if (marketingModalSessionId)
    config.headers['x-user-marketing-modal-session-id'] = marketingModalSessionId;
}

// Expand register data with all necessary parameters
function expandRegisterData(config: InternalAxiosRequestConfig): void {
  if (
    !config.url ||
    !REGISTER_ROUTES.some((route) => config.url?.includes(route)) ||
    !config.data
  ) {
    return;
  }

  const requestData = config.data as Record<string, any>;
  const updates: Record<string, any> = {};

  // Marketing modal ID
  const marketingModalSessionId = sessionStorage.getItem(
    SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID
  );
  if (marketingModalSessionId) {
    config.headers['x-user-marketing-modal-session-id'] = marketingModalSessionId;
  }

  // Referral code
  const refId = sessionStorage.getItem(SessionStorageKey.REF_ID);
  if (refId) {
    updates.referralCode = JSON.parse(refId);
  }

  // Voucher code
  const voucherShortCode = sessionStorage.getItem(SessionStorageKey.VOUCHER_SHORT_CODE);
  if (voucherShortCode) {
    updates.voucherCode = JSON.parse(voucherShortCode);
  }

  sessionStorage.removeItem(SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID);
  sessionStorage.removeItem(SessionStorageKey.REF_ID);
  config.data = { ...requestData, ...updates };
}

function authRequestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const adminToken = storage.getAdminToken();
  const token = adminToken || storage.getToken();

  const currentTenant = adminToken ? storage.getAdminTenant() : storage.getTenant();

  if (token && config.headers) {
    config.headers.authorization = `${token}`;
    config.headers.Accept = 'application/json';
  }
  if (currentTenant) {
    config.headers['x-user-novaheal-tenant-id'] = currentTenant;
  }
  expandLoginDataRequest(config);
  expandRegisterData(config);

  return config;
}

export const axios = Axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

function onResponse(response: AxiosResponse): AxiosResponse {
  if (
    response.config.url &&
    [...LOGIN_ROUTES, ...REGISTER_ROUTES].some((route) => response.config.url?.includes(route))
  ) {
    sessionStorage.removeItem(SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID);
  }
  return response;
}

/**
 * Predicate for "generic server-side failures" — HTTP 500 (Internal Server Error)
 * and 408 (Request Timeout).
 *
 * Callers use it in a `catch` block to decide whether to show the generic
 * `SERVER_ERROR` toast (when `true`) versus handling a more specific, expected
 * status themselves (e.g. 400/404/409/422).
 *
 * Note: the name is historical — it used to gate a global `errorSubject.notify`
 * that has since been removed. It no longer implies anything is handled
 * "globally"; it is purely a status check. Prefer routing new code through
 * `classifyError` / `handleMutationError` instead of calling this directly.
 */
export function caughtErrorGlobally(error: AxiosError): boolean {
  const status = error.response?.status;
  return status === 500 || status === 408;
}

function hasImpersonationRestrictionCode(error: AxiosError): boolean {
  const responseData = error.response?.data as { codeError?: string } | undefined;
  return responseData?.codeError === StatusCode.ADMIN_ACTION_NOT_ALLOWED_DURING_IMPERSONATION;
}

async function onResponseError(error: AxiosError): Promise<never> {
  if (hasImpersonationRestrictionCode(error)) {
    toaster.create({
      type: 'error',
      title: 'Method not allowed.',
      description: (error.response?.data as AxiosError)?.message,
      duration: toastDuration,
      closable: true,
    });

    return Promise.reject(error);
  }

  // Prevent infinite loop by intervening only when any requests BESIDES refresh is unauthorized.
  // this line: const tokenRefreshed = await refreshUserToken(); would cause infinite loop otherwise, as it will call refresh route to check wether it has expired tokens
  // ?? we should think about a fn that is a plain expired check on the tokens.
  const shouldTryRefetch =
    error.response?.status === 401 && !error.config?.url?.includes('/auth/refresh');

  if (storage.getAdminToken() !== null && shouldTryRefetch) {
    // If admin token is present, do not try to refresh, but log out admin and refetch user to update state
    storage.clearAdminSession();
    window.location.href = `${ProtectedRoutes.BASE}?sessionExpired=true`; // Redirect to home or login page after clearing admin token
    return Promise.reject(error);
  }
  if (error.response && shouldTryRefetch) {
    const tokenRefreshed = await refreshUserToken();
    if (tokenRefreshed && error.config) {
      return axios.request(error.config);
    }
  }

  return Promise.reject(error);
}

axios.interceptors.response.use(onResponse, onResponseError);
axios.interceptors.request.use(authRequestInterceptor);
